/* eslint-disable max-lines */
import { saveCustomer } from 'actions';
import ToggleButton from 'components/ToggleButton';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getCustomer } from 'selectors/auth';
import { useSelector } from 'store';
import {
  CustomerConfigWithPreventFields,
  formatAllowedTranslations,
  normalizeAllowedTranslations,
  parseAllowedTranslations,
  SettingRow,
  SettingsCardHeader,
} from './CustomerEditO365SettingsShared';

const CustomerEditO365PreventSettings: React.FC = () => {
  const dispatch = useDispatch();
  const customer = useSelector(getCustomer);
  const config = customer?.config as CustomerConfigWithPreventFields | undefined;
  const officeExtensionConfig = config?.office_extension_config;

  const [officeExtensionEnabled, setOfficeExtensionEnabled] = useState(
    officeExtensionConfig?.enabled ?? false
  );
  const [o365PolicyText, setO365PolicyText] = useState('');
  const [o365HeaderText, setO365HeaderText] = useState('');
  const [onsendGuidance, setOnsendGuidance] = useState('');
  const [allowRealtimeTranslation, setAllowRealtimeTranslation] = useState(false);
  const [translationConfidenceThreshold, setTranslationConfidenceThreshold] = useState('0.2');
  const [allowedTranslations, setAllowedTranslations] = useState('');
  const [allowRealtimeLlms, setAllowRealtimeLlms] = useState(false);
  const [allowRealtimeDrafts, setAllowRealtimeDrafts] = useState(false);

  const resetFromCustomer = (): void => {
    if (!config) return;

    setOfficeExtensionEnabled(config.office_extension_config?.enabled ?? false);
    setO365PolicyText(config.o365_policy_text ?? '');
    setO365HeaderText(config.o365_header_text ?? '');
    setOnsendGuidance(config.office_extension_config?.onsend_guidance ?? '');
    setAllowRealtimeTranslation(config.allow_realtime_translation ?? false);
    setTranslationConfidenceThreshold(String(config.translation_confidence_threshold ?? 0.2));
    setAllowedTranslations(formatAllowedTranslations(config.allowed_translations));
    setAllowRealtimeLlms(config.allow_realtime_llms ?? false);
    setAllowRealtimeDrafts(config.allow_realtime_drafts ?? false);
  };

  useEffect(() => {
    if (!config) return;
    resetFromCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const handleSave = (): void => {
    if (!config) return;

    const threshold = Number.parseFloat(translationConfidenceThreshold);
    if (Number.isNaN(threshold) || threshold < 0 || threshold > 1) {
      return;
    }

    dispatch(
      saveCustomer({
        config: {
          ...config,
          office_extension_config: {
            ...(config.office_extension_config ?? {}),
            enabled: officeExtensionEnabled,
            // @ts-ignore
            onsend_guidance: onsendGuidance || null,
          },
          // @ts-ignore
          o365_policy_text: o365PolicyText || null,
          o365_header_text: o365HeaderText || null,
          allow_realtime_translation: allowRealtimeTranslation,
          translation_confidence_threshold: threshold,
          allowed_translations: parseAllowedTranslations(allowedTranslations),
          allow_realtime_llms: allowRealtimeLlms,
          allow_realtime_drafts: allowRealtimeDrafts,
        },
      })
    );
  };

  const hasChanges = (): boolean => {
    if (!config) return false;

    if ((config.office_extension_config?.enabled ?? false) !== officeExtensionEnabled) {
      return true;
    }
    if ((config.o365_policy_text ?? '') !== o365PolicyText) return true;
    if ((config.o365_header_text ?? '') !== o365HeaderText) return true;
    if ((config.office_extension_config?.onsend_guidance ?? '') !== onsendGuidance) return true;
    if ((config.allow_realtime_translation ?? false) !== allowRealtimeTranslation) return true;
    if (String(config.translation_confidence_threshold ?? 0.2) !== translationConfidenceThreshold) {
      return true;
    }
    if (
      JSON.stringify(parseAllowedTranslations(allowedTranslations)) !==
      JSON.stringify(normalizeAllowedTranslations(config.allowed_translations))
    ) {
      return true;
    }
    if ((config.allow_realtime_llms ?? false) !== allowRealtimeLlms) return true;
    if ((config.allow_realtime_drafts ?? false) !== allowRealtimeDrafts) return true;

    return false;
  };

  const isFormValid = (): boolean => {
    const threshold = Number.parseFloat(translationConfidenceThreshold);
    return !Number.isNaN(threshold) && threshold >= 0 && threshold <= 1;
  };

  if (!config) {
    return null;
  }

  return (
    <div className="mt-6 bg-white border border-litlingo-gray-2 rounded">
      <SettingsCardHeader
        title="Prevent settings"
        description=""
        saveTestId="o365-prevent-save-button"
        hasChanges={hasChanges()}
        isValid={isFormValid()}
        onCancel={resetFromCustomer}
        onSave={handleSave}
      />

      <div className="px-5 py-4 border-t border-litlingo-gray-2">
        <div className="flex justify-between items-start gap-4">
          <div className="max-w-md">
            <p className="text text--medium">O365 tenant ID</p>
          </div>
          <div className="text-sm text-right break-all max-w-sm">
            {customer?.o365_tenant_id || 'Not set'}
          </div>
        </div>
      </div>

      <SettingRow
        label="Outlook extension enabled"
        description="Primary switch for enabling the Microsoft Office Prevent extension."
        testId="office-extension-enabled-row"
        control={
          <ToggleButton
            dataTestid="office-extension-enabled-toggle"
            isOn={officeExtensionEnabled}
            handleToggleClick={(): void => setOfficeExtensionEnabled((value) => !value)}
          />
        }
      />

      <div className="px-5 py-4 border-t border-litlingo-gray-2">
        <p className="text text--medium mb-2">Outlook extension config</p>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-litlingo-gray-5">Minimum version</p>
            <p>{officeExtensionConfig?.minimum_version || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-litlingo-gray-5">Target version</p>
            <p>{officeExtensionConfig?.target_version || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-litlingo-gray-5">Timeout (ms)</p>
            <p>{officeExtensionConfig?.timeout ?? '—'}</p>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 border-t border-litlingo-gray-2">
        <label className="block" htmlFor="o365-policy-text">
          <p className="text text--medium">Outlook policy text</p>
          <p className="text-xs mb-2">Policy text shown in the Outlook extension.</p>
          <textarea
            id="o365-policy-text"
            data-testid="o365-policy-text-input"
            value={o365PolicyText}
            onChange={(event): void => setO365PolicyText(event.target.value)}
            className="form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            rows={3}
          />
        </label>
      </div>

      <div className="px-5 py-4 border-t border-litlingo-gray-2">
        <label className="block" htmlFor="o365-header-text">
          <p className="text text--medium">Outlook Admin Task Pane Header Text</p>
          <p className="text-xs mb-2">
            Header text shown at the top of the Outlook admin task pane.
          </p>
          <textarea
            id="o365-header-text"
            data-testid="o365-header-text-input"
            value={o365HeaderText}
            onChange={(event): void => setO365HeaderText(event.target.value)}
            className="form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            rows={3}
          />
        </label>
      </div>

      <div className="px-5 py-4 border-t border-litlingo-gray-2">
        <label className="block" htmlFor="onsend-guidance">
          <p className="text text--medium">On Send Guidance</p>
          <p className="text-xs mb-2">
            Guidance message shown when Prevent blocks a send in Outlook.
          </p>
          <textarea
            id="onsend-guidance"
            data-testid="onsend-guidance-input"
            value={onsendGuidance}
            onChange={(event): void => setOnsendGuidance(event.target.value)}
            className="form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            rows={3}
          />
        </label>
      </div>

      <SettingRow
        label="Allow realtime translation"
        description="Enable translation during realtime Prevent evaluation."
        control={
          <ToggleButton
            dataTestid="allow-realtime-translation-toggle"
            isOn={allowRealtimeTranslation}
            handleToggleClick={(): void => setAllowRealtimeTranslation((value) => !value)}
          />
        }
      />

      <div className="px-5 py-4 border-t border-litlingo-gray-2">
        <label className="block" htmlFor="translation-confidence-threshold">
          <p className="text text--medium">Translation confidence threshold</p>
          <p className="text-xs mb-2">
            Minimum confidence score (0 to 1) required for translation.
          </p>
          <input
            id="translation-confidence-threshold"
            data-testid="translation-confidence-threshold-input"
            type="number"
            min={0}
            max={1}
            step={0.01}
            value={translationConfidenceThreshold}
            onChange={(event): void => setTranslationConfidenceThreshold(event.target.value)}
            className="form-input block w-32 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
          />
        </label>
      </div>

      <div className="px-5 py-4 border-t border-litlingo-gray-2">
        <label className="block" htmlFor="allowed-translations">
          <p className="text text--medium">Allowed translations</p>
          <p className="text-xs mb-2">
            Comma-separated language codes. Use * to allow all languages.
          </p>
          <input
            id="allowed-translations"
            data-testid="allowed-translations-input"
            type="text"
            value={allowedTranslations}
            onChange={(event): void => setAllowedTranslations(event.target.value)}
            className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            placeholder="*, es, fr"
          />
        </label>
      </div>

      <SettingRow
        label="Allow realtime LLMs"
        description="Enable LLM-based evaluation during realtime Prevent."
        control={
          <ToggleButton
            dataTestid="allow-realtime-llms-toggle"
            isOn={allowRealtimeLlms}
            handleToggleClick={(): void => setAllowRealtimeLlms((value) => !value)}
          />
        }
      />

      <SettingRow
        label="Allow realtime drafts"
        description="Enable draft evaluation during realtime Prevent."
        control={
          <ToggleButton
            dataTestid="allow-realtime-drafts-toggle"
            isOn={allowRealtimeDrafts}
            handleToggleClick={(): void => setAllowRealtimeDrafts((value) => !value)}
          />
        }
      />
    </div>
  );
};

export default CustomerEditO365PreventSettings;
